import { Box, makeStyles } from '@material-ui/core';
import { Children, CSSProperties, Fragment, ReactNode } from 'react';

type WbHeaderActionsProps = {
  children: ReactNode;
  separator?: ReactNode;
  containerStyle?: CSSProperties;
};

type Items = ReturnType<typeof Children['toArray']>;

const useStyles = makeStyles(
  theme => ({
    root: {
      display: 'flex',
      gap: '16px',
      alignItems: 'center',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        gap: '8px',
        display: 'grid',
        marginTop: '6px',
      },
    },
    rootSingleItem: {
      [theme.breakpoints.down('xs')]: {
        gridTemplateColumns: '1fr',
      },
    },
    rootMultipleItems: {
      [theme.breakpoints.down('xs')]: {
        gridTemplateColumns: '1fr 1fr',
      },
    },
    separator: {
      height: '20px',
      width: '1px',
      backgroundColor: theme.palette.softened.secondary,
      [theme.breakpoints.down('xs')]: {
        display: 'none',
      },
    },
  }),
  { name: 'WbHeaderActions' },
);

const Separator = () => {
  const classes = useStyles();
  return <Box className={classes.separator} />;
};

function insertSeparators(items: Items, separator: ReactNode) {
  return items.reduce<Items>((acc, current, index) => {
    let res = acc;
    if (index < items.length - 1) {
      res = acc.concat(
        current,
        <Fragment key={`separator${index}`}>{separator}</Fragment>,
      );
    } else {
      res.push(current);
    }

    return res;
  }, []);
}

export const WbHeaderActions = ({
  children,
  separator = <Separator />,
  containerStyle,
}: WbHeaderActionsProps) => {
  const classes = useStyles();

  const elements = Children.toArray(children);
  const separatedElements = insertSeparators(elements, separator);
  const isSingleItem = elements.length === 1;

  return (
    <Box
      className={`${classes.root} ${
        isSingleItem ? classes.rootSingleItem : classes.rootMultipleItems
      }`}
      style={containerStyle}
    >
      {separatedElements}
    </Box>
  );
};
